/**
 * Livrée de faction stylisée (phase A) : plugin des matériaux PBR.
 * - remplace la couleur de base par la teinte de faction, en gardant le relief de la texture d'origine (luminance) ;
 * - ajoute un reflet de bord (fresnel) et des bandes émissives dans le repère du modèle, dans la couleur d'accent.
 * Les modèles Meshy gardent leur forme, leur normal map et leur ombrage ; seules leurs couleurs sont remplacées.
 * Style « original » : textures et matériau Meshy intacts, seul un léger reflet de bord détache la silhouette.
 */
import {
  Color3,
  Material as BabylonMaterial,
  MaterialPluginBase,
  PBRMaterial,
  ShaderLanguage,
  type Material,
  type MaterialDefines,
  type UniformBuffer,
} from '@babylonjs/core';
import type { ArtData, Livery } from '../data';

export type LiveryStyle = ArtData['liveryStyle'];

export class LiveryPlugin extends MaterialPluginBase {
  base = new Color3(1, 1, 1);
  accent = new Color3(0, 1, 1);
  detail = 0.5;
  rim = 0.5;
  stripe = 1;
  stripeFrequency = 1;
  /** 0 = bandes, 1 = bicolore, 2 = textures teintées, 3 = textures d'origine. */
  style = 1;

  constructor(material: Material) {
    super(material, 'Livery', 200, { LIVERY: false });
    this._enable(true);
  }

  configure(livery: Livery, style: LiveryStyle): void {
    this.style = style === 'bands' ? 0 : style === 'twoTone' ? 1 : style === 'tinted' ? 2 : 3;
    this.base = new Color3(...livery.base);
    this.accent = new Color3(...livery.accent);
    this.detail = livery.detail;
    this.rim = livery.rim;
    this.stripe = livery.stripe;
    this.stripeFrequency = livery.stripeFrequency;
  }

  override prepareDefines(defines: MaterialDefines): void {
    defines['LIVERY'] = true;
  }

  override getClassName(): string {
    return 'LiveryPlugin';
  }

  override isCompatible(shaderLanguage: ShaderLanguage): boolean {
    return shaderLanguage === ShaderLanguage.GLSL;
  }

  override getUniforms() {
    return {
      ubo: [
        { name: 'liveryBase', size: 3, type: 'vec3' },
        { name: 'liveryAccent', size: 3, type: 'vec3' },
        { name: 'liveryParams', size: 4, type: 'vec4' },
        { name: 'liveryStyle', size: 1, type: 'float' },
      ],
      fragment: `#ifdef LIVERY
uniform vec3 liveryBase;
uniform vec3 liveryAccent;
uniform vec4 liveryParams;
uniform float liveryStyle;
#endif`,
    };
  }

  override bindForSubMesh(uniformBuffer: UniformBuffer): void {
    uniformBuffer.updateColor3('liveryBase', this.base);
    uniformBuffer.updateColor3('liveryAccent', this.accent);
    uniformBuffer.updateFloat4('liveryParams', this.detail, this.rim, this.stripe, this.stripeFrequency);
    uniformBuffer.updateFloat('liveryStyle', this.style);
  }

  override getCustomCode(shaderType: string): Record<string, string> {
    if (shaderType === 'vertex') {
      return {
        CUSTOM_VERTEX_DEFINITIONS: `#ifdef LIVERY
varying vec3 vLiveryLocal;
varying vec3 vLiveryNormal;
#endif`,
        CUSTOM_VERTEX_MAIN_END: `#ifdef LIVERY
vLiveryLocal = positionUpdated;
#ifdef NORMAL
vLiveryNormal = normalize(normalUpdated);
#else
vLiveryNormal = vec3(0.0, 1.0, 0.0);
#endif
#endif`,
      };
    }
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: `#ifdef LIVERY
varying vec3 vLiveryLocal;
varying vec3 vLiveryNormal;
#endif`,
      // Couleur de surface selon le style ; le relief de la texture d'origine (luminance) est conservé.
      CUSTOM_FRAGMENT_BEFORE_LIGHTS: `#ifdef LIVERY
float liveryLum = dot(surfaceAlbedo, vec3(0.299, 0.587, 0.114));
vec3 liveryOriginal = surfaceAlbedo;
if (liveryStyle < 0.5) {
  surfaceAlbedo = liveryBase * mix(1.0, clamp(0.55 + liveryLum * 1.6, 0.4, 1.4), liveryParams.x);
} else if (liveryStyle < 1.5) {
  float liveryTop = smoothstep(-0.2, 0.25, vLiveryNormal.y);
  vec3 liveryBelly = liveryBase * 0.3 + vec3(0.025);
  surfaceAlbedo = mix(liveryBelly, liveryBase, liveryTop) * mix(1.0, clamp(0.5 + liveryLum * 1.8, 0.35, 1.5), 0.8);
} else if (liveryStyle < 2.5) {
  surfaceAlbedo = mix(liveryOriginal, liveryBase * (0.35 + liveryLum * 2.2), 0.55);
}
#endif`,
      // Reflet de bord et accents émissifs : silhouette lisible sur le fond spatial.
      CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION: `#ifdef LIVERY
float liveryRim = pow(1.0 - clamp(dot(normalW, viewDirectionW), 0.0, 1.0), 3.0);
float liveryAccentMask = 0.0;
if (liveryStyle < 0.5) {
  float liveryBand = smoothstep(0.88, 0.97, abs(sin(vLiveryLocal.z * liveryParams.w)));
  liveryAccentMask = liveryBand * smoothstep(0.15, 0.55, abs(normalW.y)) * liveryParams.z * 0.3;
} else if (liveryStyle < 1.5) {
  liveryAccentMask = (1.0 - smoothstep(0.0, 0.09, abs(vLiveryNormal.y))) * liveryParams.z * 0.4;
}
float liveryRimScale = liveryStyle > 2.5 ? 0.35 : liveryStyle > 1.5 ? 0.5 : 1.0;
finalEmissive += liveryAccent * (liveryRim * liveryParams.y * liveryRimScale + liveryAccentMask);
#endif`,
    };
  }
}

/**
 * Applique (ou met à jour) la livrée d'un matériau PBR. Styles de faction : surfaces moins miroir pour rester
 * lisibles. Style « original » : facteurs métal / rugosité du modèle (mémorisés au premier passage) conservés.
 */
export function applyLivery(material: Material, livery: Livery, style: LiveryStyle = 'twoTone', environment = 1): void {
  if (!(material instanceof PBRMaterial)) return;
  material.unfreeze();
  const plugin = (material.pluginManager?.getPlugin<LiveryPlugin>('Livery')) ?? new LiveryPlugin(material);
  plugin.configure(livery, style);
  const meta = (material.metadata ??= {}) as { liveryOriginal?: { metallic: number | null; roughness: number | null } };
  meta.liveryOriginal ??= { metallic: material.metallic, roughness: material.roughness };
  if (style === 'original') {
    material.metallic = meta.liveryOriginal.metallic;
    material.roughness = meta.liveryOriginal.roughness;
  } else {
    material.metallic = 0.45;
    material.roughness = 1;
  }
  material.emissiveColor = Color3.Black();
  material.environmentIntensity = environment;
  material.markAsDirty(BabylonMaterial.AllDirtyFlag);
}
